import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useQuiz } from '../contexts/QuizContext';
import { useNavigate } from 'react-router-dom';
import { trackGatewaySelection } from '../utils/tracking';
import { useUser } from '../contexts/UserContext';
import { PageContainer, Title, Subtitle, LinkButton } from '../styles/SharedComponents';

const ResultsHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const ResultsWrapper = styled.div`
  width: 100%;
  max-width: 900px;
`;

const AnimatedCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
  }
`;

const GatewayInfo = styled.div`
  flex: 1;
`;

const GatewayName = styled.h2`
  font-size: 1.6rem;
  margin-bottom: 0.8rem;
  color: #fff;
`;

const GatewayFeatures = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 1rem;
`;

const FeatureTag = styled.span`
  background-color: #2a2a2a;
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  font-size: 0.9rem;
  white-space: nowrap;
  color: #fff;
`;

const GatewayURL = styled.p`
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  color: #6e8efb;
  word-break: break-all;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

// Lista de gateways recomendados (simulação)
const gatewayOptions = [
  {
    id: 1,
    name: 'MundPay',
    pricing: '5,99% + R$ 1,50',
    coverage: 'Global',
    payout: 'D+0',
    url: 'https://mundpay.melhortaxaglobal.com'
  },
  {
    id: 2,
    name: 'Bynet',
    pricing: '3,99%',
    coverage: 'Global',
    payout: 'D+0',
    url: 'https://bynet.melhortaxaglobal.com'
  },
  {
    id: 3,
    name: 'BlackPay',
    pricing: '3,99% + R$ 0,99',
    coverage: 'Global',
    payout: 'D+0',
    url: 'https://blackpay.melhortaxaglobal.com'
  }
];

const SimpleResults: React.FC = () => {
  const { answers, resetQuiz } = useQuiz();
  const navigate = useNavigate();
  const [selectedGateway, setSelectedGateway] = useState<typeof gatewayOptions[0] | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { user } = useUser();

  // Verificar se há respostas válidas quando o componente monta
  useEffect(() => {
    // Verificar se há respostas válidas
    const hasValidResponses = answers &&
      answers.billingVolume &&
      answers.region &&
      answers.platform &&
      answers.email &&
      answers.phone;

    // Se não houver respostas válidas, redireciona para o início
    if (!hasValidResponses) {
      resetQuiz();
      navigate('/');
      return;
    }
  }, [answers, navigate, resetQuiz]);

  // Função para lidar com o clique no gateway
  const handleGatewayClick = async (gateway: typeof gatewayOptions[0]) => {
    // Evitar cliques duplicados
    if (isRedirecting) return;
    setIsRedirecting(true);
    setSelectedGateway(gateway);

    // Verificar duplicatas usando sessionStorage
    const recentEvents = JSON.parse(sessionStorage.getItem('bt_recent_clicks') || '[]');
    const now = Date.now();
    const recentEventsFiltered = recentEvents.filter((event: any) =>
      now - event.timestamp < 3000 && // 3 segundos
      event.gatewayName === gateway.name
    );

    if (recentEventsFiltered.length > 0) {
      console.warn('Clique duplicado detectado');
      return;
    }

    // Registrar o clique atual
    recentEvents.push({
      gatewayName: gateway.name,
      timestamp: now
    });
    sessionStorage.setItem('bt_recent_clicks', JSON.stringify(recentEvents));

    // Coletar informações do dispositivo
    const deviceInfo = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };

    // Preparar dados para tracking
    const trackingData = {
      user: {
        id: user?.id || 'anonymous',
        email: user?.email || answers.email,
        name: user?.name || 'Usuário Anônimo'
      },
      search: {
        query: '',
        resultsCount: gatewayOptions.length
      },
      gateway: {
        name: gateway.name,
        url: gateway.url,
        position: gatewayOptions.findIndex(g => g.name === gateway.name) + 1
      },
      device: deviceInfo,
      session: {
        startTime: sessionStorage.getItem('bt_session_start') || Date.now(),
        currentTime: Date.now()
      }
    };

    try {
      // Enviar tracking
      await trackGatewaySelection(gateway.name, gateway.url, trackingData);
    } catch (error) {
      console.warn('Erro ao enviar tracking:', error);
    }

    // Redirecionar após um pequeno delay para garantir que o tracking seja tentado
    setTimeout(() => {
      window.location.href = gateway.url;
    }, 1000);
  };

  return (
    <PageContainer>
      <ResultsHeader>
        {isRedirecting ? (
          <>
            <Title>
              Redirecionando para {selectedGateway?.name}...
            </Title>
            <Subtitle>
              Estamos te conectando com o melhor gateway para o seu negócio. Aguarde...
            </Subtitle>
          </>
        ) : (
          <>
            <Title>
              Seus Gateways Recomendados
            </Title>
            <Subtitle>
              Com base nas suas respostas, estes são os melhores gateways de pagamento para o seu negócio:
            </Subtitle>
          </>
        )}
      </ResultsHeader>

      {!isRedirecting && (
        <ResultsWrapper>
          {gatewayOptions.map((gateway, index) => (
            <AnimatedCard
              key={gateway.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <GatewayInfo>
                <GatewayName>
                  {index + 1}. {gateway.name}
                </GatewayName>
                <GatewayFeatures>
                  <FeatureTag>{gateway.pricing}</FeatureTag>
                  <FeatureTag>{gateway.coverage}</FeatureTag>
                  <FeatureTag>{gateway.payout}</FeatureTag>
                </GatewayFeatures>
                <GatewayURL>{gateway.url}</GatewayURL>
              </GatewayInfo>
              <ButtonWrapper>
                <LinkButton
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleGatewayClick(gateway);
                  }}
                >
                  Saiba mais
                </LinkButton>
              </ButtonWrapper>
            </AnimatedCard>
          ))}
        </ResultsWrapper>
      )}
    </PageContainer>
  );
};

export default SimpleResults; 